import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Mail, CheckCircle, RefreshCw, Edit3, X, Loader2, 
  Sparkles, HelpCircle, ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { user, isEmailVerified, resendVerificationEmail, reloadUser, changeUserEmail, logout } = useAuth();
  
  const [isChecking, setIsChecking] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [showEmailEdit, setShowEmailEdit] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [displayedEmail, setDisplayedEmail] = useState(user?.email || "");

  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  // If already verified, go to onboarding
  useEffect(() => {
    if (isEmailVerified) {
      navigate("/onboarding");
    }
  }, [isEmailVerified, navigate]);

  // If no user, go to login
  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

 const handleCheckVerification = async () => {
    setIsChecking(true);
    
    // 1. Recarrega os dados do servidor
    const result = await reloadUser();
    
    setIsChecking(false);

    // 2. Checamos o valor que ACABOU de voltar da função (result.emailVerified)
    if (result.success && result.emailVerified) {
      toast.success("Boa! E-mail confirmado. O porquinho agradece! 🐷");
      navigate("/onboarding");
    } else {
      toast.error("Ainda não confirmou... O link tá lá no seu e-mail, vai lá ver!");
    }
  };

  const handleResendEmail = async () => {
    if (cooldown > 0) return;
    
    setIsResending(true);
    const result = await resendVerificationEmail();
    setIsResending(false);

    if (result.success) {
      toast.success("Link reenviado! Confere lá (inclusive o spam). 📬");
      setCooldown(60);
    } else {
      toast.error(result.error);
    }
  };

  const handleUpdateEmail = async () => {
    if (!newEmail.trim() || !newEmail.includes("@")) {
      toast.error("Ops! Esse e-mail não parece válido...");
      return;
    }

    if (newEmail === user?.email) {
      toast.error("Esse é o mesmo e-mail! Tenta um diferente.");
      return;
    }

    setIsUpdatingEmail(true);
    // TRATAMENTO: Limpa espaços e padroniza para minúsculo
    const cleanNewEmail = newEmail.trim().toLowerCase();
    const result = await changeUserEmail(cleanNewEmail);
    setIsUpdatingEmail(false);

    if (result.success) {
      toast.success("Novo link enviado para o e-mail atualizado! 📧");
      setDisplayedEmail(newEmail); // Isso corrige o erro de não atualizar a tela
      setShowEmailEdit(false);
      setNewEmail("");
      setCooldown(60);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white px-6 py-8">
      <div className="max-w-md mx-auto">
        
        {/* Header Illustration */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex justify-center mb-8"
        >
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-xl shadow-indigo-500/30">
              <Mail className="w-12 h-12 text-white" />
            </div>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="absolute -top-1 -right-1 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center shadow-lg"
            >
              <Sparkles className="w-4 h-4 text-white" />
            </motion.div>
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-8"
        >
          <h1 className="text-2xl font-black tracking-tight text-slate-900 mb-3">
            Um segundinho!
          </h1>
          <p className="text-slate-600">
            Antes de continuar, precisamos confirmar se esse e-mail é seu.
            Confere lá na sua caixa de entrada e clique no link que enviamos.
          </p>
        </motion.div>

        {/* Email Display */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-[24px] p-5 shadow-sm border border-slate-100 mb-6"
        >
          <p className="text-sm text-slate-500 mb-2">Enviamos um link para:</p>
          <div className="flex items-center justify-between">
            <p className="text-lg font-bold text-indigo-600 break-all" data-testid="verify-email-address">
              {displayedEmail}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-slate-600 flex-shrink-0"
              onClick={() => setShowEmailEdit(!showEmailEdit)}
              data-testid="edit-email-btn"
            >
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>

        {/* Edit Email Section */}
        <AnimatePresence>
          {showEmailEdit && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mb-6"
            >
              <div className="bg-amber-50 rounded-[20px] p-4 border border-amber-200">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-semibold text-amber-800">
                    Digitei o e-mail errado
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-amber-600"
                    onClick={() => setShowEmailEdit(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="novo@email.com"
                    className="h-10 rounded-xl bg-white border-amber-200"
                    data-testid="new-email-input"
                  />
                  <Button
                    onClick={handleUpdateEmail}
                    disabled={isUpdatingEmail || !newEmail.trim()}
                    className="h-10 px-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="update-email-btn"
                  >
                    {isUpdatingEmail ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Atualizar"
                    )}
                  </Button>
                </div>
                <p className="text-xs text-amber-700 mt-2">
                  Vamos enviar um novo link de verificação para o e-mail atualizado.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-3 mb-8"
        >
          {/* Primary: Check Verification */}
          <Button
            onClick={handleCheckVerification}
            disabled={isChecking}
            className="w-full h-14 rounded-full font-bold bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white shadow-lg shadow-indigo-500/30"
            data-testid="check-verification-btn"
          >
            {isChecking ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Verificando...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Já verifiquei!
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>

          {/* Secondary: Resend */}
          <Button
            variant="outline"
            onClick={handleResendEmail}
            disabled={isResending || cooldown > 0}
            className="w-full h-12 rounded-full font-semibold border-2"
            data-testid="resend-email-btn"
          >
            {isResending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : cooldown > 0 ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reenviar em {cooldown}s
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reenviar link
              </>
            )}
          </Button>
        </motion.div>

        {/* Help Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-50 rounded-[20px] p-5 border border-slate-100"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
              <HelpCircle className="w-5 h-5 text-slate-500" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 mb-2">Não achou o e-mail?</h3>
              <p className="text-sm text-slate-600 mb-3">
                Às vezes ele se esconde na <span className="font-semibold">pasta de spam</span>. 
                Dá uma olhada lá!
              </p>
              <p className="text-sm text-slate-500">
                Se o link sumiu ou você está com dificuldades, não entra em pânico! 
                Manda um alô em{" "}
                <a 
                  href="mailto:contato@apptroquinho.com.br" 
                  className="text-indigo-600 font-semibold hover:underline"
                >
                  contato@apptroquinho.com.br
                </a>
                {" "}que a gente te ajuda.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Logout option */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center mt-8"
        >
          <button
            onClick={handleLogout}
            className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="logout-verify-btn"
          >
            Usar outro e-mail? <span className="font-semibold underline">Sair e criar nova conta</span>
          </button>
        </motion.div>

        {/* Fun footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-xs text-slate-400 mt-6 italic"
        >
          "Verificar e-mail é chato, mas confiar em estranhos é pior."
        </motion.p>
      </div>
    </div>
  );
}
